/**
 * Billin sign-in: เก็บ JWT ชั่วคราวระหว่างสร้าง workspace
 * — commit ลง localStorage + cookie เมื่อกด「เริ่มงานเลย」เท่านั้น
 */

export const SIGNIN_PENDING_AUTH_TOKEN_KEY = "signin_pending_auth_token";
export const SIGNIN_PENDING_USER_KEY = "signin_pending_user";
export const SIGNIN_BILLIN_ONBOARDING_ACTIVE_KEY =
  "signin_billin_onboarding_active_v1";

export function getSigninCommittedAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const t = localStorage.getItem("auth_token");
    return t?.trim() ? t.trim() : null;
  } catch {
    return null;
  }
}

export function getSigninPendingAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const t = sessionStorage.getItem(SIGNIN_PENDING_AUTH_TOKEN_KEY);
    return t?.trim() ? t.trim() : null;
  } catch {
    return null;
  }
}

/** Token สำหรับ API ระหว่าง onboarding (pending ก่อน, แล้วค่อย committed) */
export function getSigninAuthTokenForApi(): string | null {
  return getSigninCommittedAuthToken() || getSigninPendingAuthToken();
}

export function hasSigninCommittedAuthToken(): boolean {
  return Boolean(getSigninCommittedAuthToken());
}

/** มี JWT ชั่วคราว (pending) หรือ committed แล้ว */
export function hasSigninAuthSession(): boolean {
  return Boolean(getSigninAuthTokenForApi());
}

async function clearAuthCookie(): Promise<void> {
  try {
    await fetch("/api/auth/set-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ clear: true }),
      credentials: "same-origin",
    });
  } catch {
    // ignore
  }
}

/** เก็บ JWT ชั่วคราวสำหรับเรียก API (เช็ค workspace/order) — ยังไม่เปิดฟอร์ม onboarding */
export async function stageSigninPendingAuthToken(
  token: string,
  user?: Record<string, unknown> | null,
): Promise<void> {
  if (typeof window === "undefined") return;
  const trimmed = token.trim();
  if (!trimmed) return;

  try {
    sessionStorage.setItem(SIGNIN_PENDING_AUTH_TOKEN_KEY, trimmed);
    if (user && typeof user === "object" && !("error" in user && user.error)) {
      sessionStorage.setItem(SIGNIN_PENDING_USER_KEY, JSON.stringify(user));
    }
  } catch {
    // ignore
  }

  try {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
  } catch {
    // ignore
  }

  await clearAuthCookie();

  try {
    window.dispatchEvent(new Event("signin:pending-auth"));
  } catch {
    // ignore
  }
}

export function markSigninBillinOnboardingActive(): void {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(SIGNIN_BILLIN_ONBOARDING_ACTIVE_KEY, "1");
  } catch {
    // ignore
  }
}

/** ระหว่างสร้าง workspace — ยังไม่ถือว่า login ทั้งแอป */
export async function stageSigninAuthForBillinOnboarding(
  token: string,
  user?: Record<string, unknown> | null,
): Promise<void> {
  await stageSigninPendingAuthToken(token, user);
  markSigninBillinOnboardingActive();
}

/** หลังสร้าง workspace สำเร็จ — เก็บ JWT ถาวร */
export async function commitSigninAuthSession(
  token: string,
  user?: Record<string, unknown> | null,
): Promise<void> {
  if (typeof window === "undefined") return;
  const trimmed = token.trim();
  if (!trimmed) return;

  try {
    sessionStorage.removeItem(SIGNIN_PENDING_AUTH_TOKEN_KEY);
    sessionStorage.removeItem(SIGNIN_PENDING_USER_KEY);
    sessionStorage.removeItem(SIGNIN_BILLIN_ONBOARDING_ACTIVE_KEY);
  } catch {
    // ignore
  }

  try {
    localStorage.setItem("auth_token", trimmed);
    if (user && typeof user === "object") {
      localStorage.setItem("user", JSON.stringify(user));
    }
  } catch {
    // ignore
  }

  try {
    await fetch("/api/auth/set-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: trimmed }),
      credentials: "same-origin",
    });
  } catch {
    // ignore
  }

  try {
    window.dispatchEvent(new Event("auth:login"));
    window.dispatchEvent(new Event("auth:loginSuccess"));
  } catch {
    // ignore
  }
}

/** กดกลับ / ปิด onboarding ก่อนสร้าง workspace เสร็จ */
export async function discardSigninPendingAuth(): Promise<void> {
  if (typeof window === "undefined") return;

  try {
    sessionStorage.removeItem(SIGNIN_PENDING_AUTH_TOKEN_KEY);
    sessionStorage.removeItem(SIGNIN_PENDING_USER_KEY);
    sessionStorage.removeItem(SIGNIN_BILLIN_ONBOARDING_ACTIVE_KEY);
  } catch {
    // ignore
  }

  try {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("user");
  } catch {
    // ignore
  }

  await clearAuthCookie();

  try {
    window.dispatchEvent(new Event("auth:logout"));
  } catch {
    // ignore
  }
}

export function isSigninBillinOnboardingActive(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return (
      sessionStorage.getItem(SIGNIN_BILLIN_ONBOARDING_ACTIVE_KEY) === "1" &&
      Boolean(getSigninPendingAuthToken())
    );
  } catch {
    return false;
  }
}
